import React, { useState } from 'react';
import { useAssessment } from '../../context/AssessmentContext';
import ProgressPath from '../ui/ProgressPath';
import ParticleEffect from '../ui/ParticleEffect';
import AnimatedBackground from '../ui/AnimatedBackground';
import { Flame, Heart, CloudFog, Flower2Icon, LeafIcon, BoxIcon } from 'lucide-react';
const HealingIntent: React.FC = () => {
  const {
    currentStep,
    setCurrentStep,
    assessmentData,
    updateAssessmentData
  } = useAssessment();
  const [selectedIntent, setSelectedIntent] = useState<string>(assessmentData.primaryHealing || '');
  const healingIntents = [{
    id: 'phoenix',
    name: 'Phoenix',
    icon: Flame,
    description: 'Resilience & Rebirth',
    detail: 'Rising from past challenges to create a new beginning',
    color: 'from-orange-500/30 to-red-500/30',
    border: 'border-orange-300'
  }, {
    id: 'dove',
    name: 'Dove',
    icon: Heart,
    description: 'Compassion & Peace',
    detail: 'Cultivating self-love and inner harmony',
    color: 'from-pink-500/30 to-purple-500/30',
    border: 'border-pink-300'
  }, {
    id: 'wolf',
    name: 'BoxIcon',
    icon: BoxIcon,
    description: 'Authenticity & Boundaries',
    detail: 'Honoring your true nature and protecting your energy',
    color: 'from-indigo-500/30 to-blue-500/30',
    border: 'border-indigo-300'
  }, {
    id: 'butterfly',
    name: 'BoxIcon',
    icon: BoxIcon,
    description: 'Transformation & Joy',
    detail: 'Embracing change and finding lightness in your journey',
    color: 'from-yellow-500/30 to-green-500/30',
    border: 'border-yellow-300'
  }, {
    id: 'eagle',
    name: 'BoxIcon',
    icon: BoxIcon,
    description: 'Vision & Purpose',
    detail: 'Gaining clarity on your path and higher perspective',
    color: 'from-blue-500/30 to-cyan-500/30',
    border: 'border-blue-300'
  }, {
    id: 'owl',
    name: 'Owl',
    icon: CloudFog,
    description: 'Wisdom & Intuition',
    detail: 'Developing inner knowing and spiritual insight',
    color: 'from-purple-500/30 to-violet-500/30',
    border: 'border-purple-300'
  }, {
    id: 'dragon',
    name: 'BoxIcon',
    icon: BoxIcon,
    description: 'Power & Integration',
    detail: 'Reclaiming your strength and integrating shadow aspects',
    color: 'from-emerald-500/30 to-teal-500/30',
    border: 'border-emerald-300'
  }];
  const handleContinue = () => {
    if (selectedIntent) {
      updateAssessmentData({
        primaryHealing: selectedIntent
      });
      setCurrentStep(currentStep + 1);
    }
  };
  return <div className="min-h-screen flex flex-col items-center justify-center px-4 py-12 relative" data-id="element-626">
      <AnimatedBackground type="cosmic" data-id="element-627" />
      <ParticleEffect count={50} data-id="element-628" />
      {/* Decorative elements */}
      <div className="absolute top-10 left-10 garden-leaf opacity-20" data-id="element-629">
        <LeafIcon size={80} className="text-green-200" data-id="element-630" />
      </div>
      <div className="absolute bottom-20 right-20 garden-leaf opacity-20" data-id="element-631">
        <LeafIcon size={100} className="text-green-200" data-id="element-632" />
      </div>
      <div className="absolute top-1/4 right-1/4 garden-flower opacity-20" data-id="element-633">
        <Flower2Icon size={60} className="text-pink-200" data-id="element-634" />
      </div>
      <div className="w-full max-w-4xl" data-id="element-635">
        <h1 className="text-4xl md:text-5xl font-bold text-center text-white mb-2" data-id="element-636">
          <span className="text-pink-300" data-id="element-637">Seven</span>After
        </h1>
        <ProgressPath totalSteps={7} currentStep={0} data-id="element-638" />
        <div className="bg-white bg-opacity-10 backdrop-blur-md rounded-2xl p-6 md:p-8 border border-white border-opacity-20 shadow-xl relative overflow-hidden" data-id="element-639">
          <div className="spiral-pattern absolute inset-0 opacity-20 z-0" data-id="element-640"></div>
          <div className="lotus-pattern absolute inset-0 opacity-20 z-0" data-id="element-641"></div>
          <h2 className="text-2xl md:text-3xl font-bold text-center text-white mb-4 relative z-10" data-id="element-642">
            What Calls to Your Soul?
          </h2>
          <p className="text-white text-center mb-6 relative z-10" data-id="element-643">
            Choose the healing archetype that resonates most deeply with your
            current journey. This will guide your path through our sacred
            gardens.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 relative z-10" data-id="element-644">
            {healingIntents.map(intent => {
            const Icon = intent.icon;
            const isSelected = selectedIntent === intent.id;
            return <button key={intent.id} onClick={() => setSelectedIntent(intent.id)} className={`
                    p-4 rounded-xl text-left transition-all border backdrop-blur-sm
                    ${isSelected ? `bg-gradient-to-br ${intent.color} ${intent.border} border-opacity-70 shadow-lg` : 'bg-white bg-opacity-5 border-white border-opacity-20 hover:bg-opacity-10'}
                  `} data-id="element-645">
                  <div className="flex items-center mb-2" data-id="element-646">
                    <div className={`
                      w-12 h-12 rounded-full flex items-center justify-center
                      bg-gradient-to-br ${intent.color} relative
                    `} data-id="element-647">
                      <Icon size={24} className="text-white" data-id="element-648" />
                      {isSelected && <div className="absolute w-full h-full rounded-full animate-ping bg-white opacity-30" data-id="element-649"></div>}
                    </div>
                    <div className="ml-3" data-id="element-650">
                      <h3 className="text-xl font-bold text-white" data-id="element-651">
                        {intent.name}
                      </h3>
                      <p className="text-white text-opacity-80" data-id="element-652">
                        {intent.description}
                      </p>
                    </div>
                  </div>
                  <p className="text-white text-opacity-70 text-sm" data-id="element-653">
                    {intent.detail}
                  </p>
                </button>;
          })}
          </div>
          <button onClick={handleContinue} disabled={!selectedIntent} className={`
              w-full py-4 rounded-xl transition-all duration-300 text-white font-semibold relative z-10
              ${selectedIntent ? 'mystical-button glow bg-gradient-to-r from-pink-500 to-orange-500 hover:shadow-lg cursor-pointer' : 'bg-gray-500 bg-opacity-50 cursor-not-allowed'}
            `} data-id="element-654">
            Continue Your Sacred Journey
          </button>
          {!selectedIntent && <p className="text-center text-white text-opacity-60 mt-3 text-sm relative z-10" data-id="element-655">
              Please select a healing archetype to continue
            </p>}
        </div>
      </div>
    </div>;
};
export default HealingIntent;